import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { NotFoundView, DefaultRouteError } from "@/components/RouteFallbacks";
import { FaqSection, faqSchema } from "@/components/PageFaq";
import { TECH_BY_SLUG, TECH_NODES, type TechNode } from "@/lib/tech-data";

function techFaq(n: TechNode) {
  const points = n.tier === "Ancient" ? "Ancient Technology Points" : "Technology Points";
  return [
    {
      q: `What level do you unlock ${n.name} in Palworld?`,
      a: `${n.name} unlocks at level ${n.lvl} in Palworld and costs ${n.cost} ${points}.`,
    },
    {
      q: `Does ${n.name} need a prerequisite in Palworld?`,
      a: n.needs
        ? `Yes — you must unlock ${n.needs} before ${n.name} becomes available.`
        : `No — ${n.name} has no prerequisite unlock, so you can take it as soon as you reach level ${n.lvl}.`,
    },
    { q: `Is ${n.name} worth the tech points in Palworld?`, a: n.note },
    {
      q: `How do you get ${points} in Palworld?`,
      a:
        n.tier === "Ancient"
          ? "Ancient Technology Points come from defeating Alpha Pals and tower bosses — one point per new Alpha, so clear the map's Alphas before spending."
          : "Technology Points come from levelling up — one per level — and can be reset at a Statue of Power if you misspend them.",
    },
  ];
}

export const Route = createFileRoute("/technology-tree/$slug")({
  loader: ({ params }): { node: TechNode } => {
    const node = TECH_BY_SLUG[params.slug];
    if (!node) throw notFound();
    return { node };
  },
  head: ({ loaderData }) => {
    if (!loaderData)
      return { meta: [{ title: "Tech not found — Pal Stars" }, { name: "robots", content: "noindex" }] };
    const n = loaderData.node;
    const long = `Palworld ${n.name}: Unlock Level & Cost`;
    const title = long.length <= 60 ? long : `Palworld ${n.name} Unlock`.slice(0, 60);
    const base = `${n.name} unlocks at level ${n.lvl} in Palworld for ${n.cost} ${n.tier === "Ancient" ? "Ancient Technology" : "Technology"} Points. ${n.note}`;
    const desc = base.length <= 155 ? base : base.slice(0, 152).replace(/\s+\S*$/, "") + "…";
    const url = `https://palstars.com/technology-tree/${n.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        faqSchema(techFaq(n), url),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "@id": `${url}#article`,
            headline: `Palworld ${n.name} — level ${n.lvl} ${n.tier} unlock`,
            description: desc,
            mainEntityOfPage: url,
            author: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com/" },
            publisher: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com/" },
            keywords: [
              `palworld ${n.name.toLowerCase()}`,
              `palworld ${n.name.toLowerCase()} unlock level`,
              `palworld ${n.tier.toLowerCase()} technology`,
            ].join(", "),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": `${url}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://palstars.com/" },
              {
                "@type": "ListItem",
                position: 2,
                name: "Technology Tree",
                item: "https://palstars.com/technology-tree",
              },
              { "@type": "ListItem", position: 3, name: n.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: TechDetail,
  errorComponent: DefaultRouteError,
  notFoundComponent: () => (
    <NotFoundView
      title="That technology isn't in our tree"
      message="No Palworld technology matches this URL. Browse the full technology tree for every Tech and Ancient Tech unlock with its level gate and point cost."
    />
  ),
});

function TechDetail() {
  const { node: n } = Route.useLoaderData();
  const faq = techFaq(n);
  const sameCat = TECH_NODES.filter((x) => x.cat === n.cat && x.slug !== n.slug).slice(0, 6);
  const points = n.tier === "Ancient" ? "Ancient Technology Points" : "Technology Points";

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>{" "}
        /{" "}
        <Link to="/technology-tree" className="hover:text-foreground">
          Technology Tree
        </Link>{" "}
        / <span className="text-foreground">{n.name}</span>
      </nav>

      <span className="text-xs font-semibold uppercase tracking-widest text-primary">
        {n.tier} · {n.cat}
      </span>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld {n.name}</h1>
      <p className="aeo-answer mt-3 text-muted-foreground">
        {n.name} unlocks at level {n.lvl} in Palworld and costs {n.cost} {points}. {n.note}
      </p>

      <dl className="mt-6 grid gap-3 sm:grid-cols-2">
        <div className="glass rounded-xl p-4">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Unlock level</dt>
          <dd className="mt-1 font-semibold">Level {n.lvl}</dd>
        </div>
        <div className="glass rounded-xl p-4">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Cost</dt>
          <dd className="mt-1 font-semibold">
            {n.cost} {points}
          </dd>
        </div>
        <div className="glass rounded-xl p-4">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Tier</dt>
          <dd className="mt-1 font-semibold">{n.tier}</dd>
        </div>
        <div className="glass rounded-xl p-4">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Prerequisite</dt>
          <dd className="mt-1 font-semibold">{n.needs ?? "None"}</dd>
        </div>
      </dl>

      <FaqSection items={faq} heading={`${n.name} FAQ`} />

      {sameCat.length > 0 && (
        <nav aria-label={`Other Palworld ${n.cat} unlocks`} className="glass rounded-2xl p-6 mt-8">
          <h2 className="text-xl font-bold">Other Palworld {n.cat.toLowerCase()} unlocks</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-sm">
            {sameCat.map((r) => (
              <li key={r.slug}>
                <Link
                  to="/technology-tree/$slug"
                  params={{ slug: r.slug }}
                  className="font-semibold text-primary hover:underline"
                >
                  Palworld {r.name} (level {r.lvl})
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm">
            <Link to="/technology-tree" className="text-primary hover:underline">
              Back to the full Palworld technology tree →
            </Link>
          </p>
        </nav>
      )}
    </div>
  );
}
